import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useRef, useCallback } from 'react';
import { queryClient, cacheUtils } from '../queryClient';
import { showAlert } from '../utils/alert';
import { summarizeFieldErrors } from '../utils/mapFieldErrors';

interface MutationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidateKeys?: ReadonlyArray<readonly unknown[]>;
  successMessage?: string;
  errorMessage?: string;
  onSuccessCallback?: (data: TData, variables: TVariables) => void | Promise<void>;
  onErrorCallback?: (error: any, variables: TVariables) => void;
  // KD3 S2499: error ที่หน้าจอจัดการเองเป็นขั้นตอนปกติ (เช่น 409 ขอให้ยืนยันก่อน) → ไม่ขึ้น toast
  // "เกิดข้อผิดพลาด" ซ้อนกับกล่องยืนยัน · callback ของ caller ยังถูกเรียกตามเดิม
  isHandledError?: (error: any) => boolean;
}

/**
 * Custom hook ที่รวม mutation กับการ refresh ข้อมูลอัตโนมัติ
 * ใช้กับทุกหน้าเพื่อให้ข้อมูลอัพเดททันที
 */
export function useMutationWithRefresh<TData = unknown, TVariables = void>(
  config: MutationConfig<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn'>
) {
  // BUG-S795 systemic (S796): synchronous in-flight guard กัน rapid double-click ยิง .mutate()
  // 2 ครั้ง same-tick (react-query isPending re-render delay กันไม่ทัน · เดียวกับ BUG-S526/BUG-S795)
  // guard เฉพาะ .mutate() (fire-and-forget) — ไม่แตะ mutateAsync ที่ caller await รับ return value
  const inFlightRef = useRef(false);

  const mutation = useMutation<TData, Error, TVariables>({
    mutationFn: config.mutationFn,

    onSuccess: async (data, variables) => {
      // 1. แสดง success message
      if (config.successMessage) {
        console.log('🔔 useMutationWithRefresh successMessage:', config.successMessage);
        showAlert('สำเร็จ', config.successMessage, 'success');
      }
      
      // 2. บังคับ refetch queries ที่เกี่ยวข้องทันที
      if (config.invalidateKeys && config.invalidateKeys.length > 0) {
        console.log('🔄 Invalidating and refetching queries:', config.invalidateKeys);
        
        // ใช้ invalidate + refetch เพื่อให้แน่ใจว่าข้อมูลอัพเดท
        const promises = config.invalidateKeys.map(async (keys) => {
          console.log('🔄 Processing key:', keys);
          
          // 1. Invalidate ก่อน
          await queryClient.invalidateQueries({ 
            queryKey: keys,
            type: 'all'
          });
          
          // 2. Refetch ทันที
          const result = await queryClient.refetchQueries({ 
            queryKey: keys,
            type: 'all',
            stale: true // บังคับ refetch แม้ข้อมูลจะไม่ stale
          });
          
          console.log('✅ Invalidated and refetched for', keys, ':', result);
          return result;
        });
        
        await Promise.all(promises);
        console.log('✅ All queries invalidated and refetched');
        
        // เพิ่ม delay เล็กน้อยเพื่อให้ UI อัพเดท
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // 3. Custom callback
      if (config.onSuccessCallback) {
        console.log('🔔 useMutationWithRefresh onSuccessCallback called');
        await config.onSuccessCallback(data, variables);
      }
      
      // 4. Run additional onSuccess from options
      if (options?.onSuccess) {
        console.log('🔔 useMutationWithRefresh options.onSuccess called');
        await options.onSuccess(data, variables, undefined);
      }
    },
    
    onError: (error: any, variables) => {
      // 1. แสดง error message - ดึงข้อมูลจากรูปแบบใหม่ของ backend
      let message = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      let title = 'เกิดข้อผิดพลาด';
      
      // S1513 OBS-S1513-003 FIX: gated to DEV-only — error.response.data may contain
      // server-side validation messages with user input echoed back (PII concern).
      if (import.meta.env.DEV) {
        console.log('🔴 Full error object:', error);
        console.log('🔴 Response data:', error.response?.data);
        console.log('🔴 Response status:', error.response?.status);
      }
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // ลองดึงจากรูปแบบใหม่ของ error response
        if (errorData.error?.user_message) {
          message = errorData.error.user_message;
        } else if (errorData.error?.message) {
          message = errorData.error.message;
        } else if (errorData.detail) {
          message = errorData.detail;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (config.errorMessage) {
          message = config.errorMessage;
        }
        
        // feedback ทิวากร 3 ก.ย. 2026: 422 เดิมโชว์ user_message รวม "กรุณาตรวจสอบข้อมูลที่กรอก
        // และลองใหม่อีกครั้ง" ซึ่งไม่บอกว่าช่องไหนผิด (และฟอร์ม snake_case ก็ไม่ได้ error ใต้ช่อง)
        // → ถ้ามี field_errors ให้สรุปข้อความจริงของแต่ละช่องขึ้น toast
        const fieldSummary = summarizeFieldErrors(error);
        if (fieldSummary) {
          message = fieldSummary;
          title = 'ข้อมูลไม่ถูกต้อง';
        }

        // ปรับ title ตามประเภท error และ error code
        if (error.response.status === 400) {
          // ตรวจสอบ error code เพื่อใช้ title ที่เหมาะสม
          if (errorData.error?.code === 'BUSINESS_RULE_VIOLATION') {
            title = 'ไม่สามารถดำเนินการได้';
          } else {
            title = 'ข้อมูลไม่ถูกต้อง';
          }
        } else if (error.response.status === 401) {
          title = 'ไม่มีสิทธิ์เข้าถึง';
        } else if (error.response.status === 403) {
          title = 'การเข้าถึงถูกปฏิเสธ';
        } else if (error.response.status === 404) {
          title = 'ไม่พบข้อมูล';
        } else if (error.response.status >= 500) {
          title = 'ข้อผิดพลาดจากเซิร์ฟเวอร์';
        }
      } else if (error.message?.includes('Network Error')) {
        title = 'ปัญหาการเชื่อมต่อ';
        message = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      } else if (config.errorMessage) {
        message = config.errorMessage;
      }
      
      console.log('🔴 Final error message:', message);
      console.log('🔴 Final error title:', title);
      
      if (!config.isHandledError?.(error)) {
        showAlert(title, message, 'error');
      }

      // KD3 S2110 (BUG-S2110-001): 404 = แถวนี้ถูกลบ/หายไปจากที่อื่นแล้ว (อีกแท็บ/อีกคน) — เดิมไม่รีเฟรช
      // รายการ แถวผียังค้างบนจอ ผู้ใช้กดซ้ำก็ได้ 404 วนไป · รีเฟรช query ของหน้าเพื่อให้แถวหายตามจริง
      if (error.response?.status === 404 && config.invalidateKeys?.length) {
        config.invalidateKeys.forEach((keys) => {
          queryClient.invalidateQueries({ queryKey: keys as unknown[] });
        });
      }

      // 2. Custom error callback
      if (config.onErrorCallback) {
        config.onErrorCallback(error, variables);
      }
      
      // 3. Run additional onError from options
      if (options?.onError) {
        options.onError(error, variables, undefined);
      }
    },
    
    ...options,

    // reset guard เมื่อ mutation จบ (success หรือ error) → ครั้งถัดไปยิงได้ปกติ · วางหลัง ...options ให้ชนะ
    onSettled: (data, error, variables, context) => {
      inFlightRef.current = false;
      (options as any)?.onSettled?.(data, error, variables, context);
    },
  });

  // wrap .mutate() ด้วย synchronous guard — บล็อกการเรียกซ้ำ same-tick ขณะ mutation แรกยัง in-flight
  const guardedMutate = useCallback((variables: TVariables, opts?: any) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    return mutation.mutate(variables, opts);
  }, [mutation]);

  return { ...mutation, mutate: guardedMutate as typeof mutation.mutate };
}

/**
 * ตัวอย่างการใช้งาน:
 * 
 * const createBrandMutation = useMutationWithRefresh({
 *   mutationFn: (data) => brandService.create(data),
 *   invalidateKeys: [['brands'], ['products']], // refresh ทั้ง brands และ products
 *   successMessage: 'สร้างยี่ห้อสำเร็จ',
 *   errorMessage: 'ไม่สามารถสร้างยี่ห้อได้'
 * });
 * 
 * // ใช้งาน
 * createBrandMutation.mutate(formData);
 */